import { useAtom } from '@reatom/npm-react'
import { useLayoutEffect, useRef, useState } from 'react'
import { Outlet, useLocation, useMatch } from 'react-router'
import { useEffectOnce } from 'react-use'
import styled from 'styled-components'

import { scrollPageToTopEvent } from '@root/entities/platform'
import { OtpDialogController } from '@root/features/auth/setup-otp/ui/desktop/enter-otp-dialog'
import { CreateCurrencyModal } from '@root/features/create-currency'
import { RootRoute } from '@root/features/routing'
import { PageScrollContext } from '@root/shared/layout/page-scroll-context'
import { TauriEnvironment } from '@root/shared/layout/tauri-environment/tauri-environment'
import { TOPBAR_HEIGHT } from '@root/shared/ui-kit/utils/page-layout'
import { AppTitleBar } from '@root/widgets/desktop/app-titlebar'
import { FavoritesDrawer } from '@root/widgets/desktop/favorite-drawer'
import { SearchModal } from '@root/widgets/desktop/search-modal'
import { PremiumBuyDialogs } from '@root/widgets/premium/premium-payment/ui/desktop'
import { SplashPointsInfoDialog } from '@root/widgets/splash-points/ui/splash-points-info-dialog'
import { TradeSettingsModalController } from '@root/widgets/trade/trade-settings/desktop/ui/trade-settings-modal-controller'

import { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_EXPANDED_WIDTH } from './constants'
import { navigationExpandedAtom, navigationLockedAtom } from './sidebar/atoms'
import { Sidebar } from './sidebar/sidebar'
import { StatusBar } from './status-bar'
import { Topbar } from './topbar/topbar'

const DESKTOP_LAYOUT_Z_INDEX = 200
type Props = {
  withTitleBar?: boolean
}

export const DesktopLayout: React.FC<Props> = ({ withTitleBar }) => {
  const [locked] = useAtom(navigationLockedAtom)
  const [expanded] = useAtom(navigationExpandedAtom)
  const isExplorePage = Boolean(useMatch({ path: RootRoute.path, end: true }))
  const { pathname } = useLocation()
  const scrollableRef = useRef<HTMLDivElement>(null)
  const [pageScrollElement, setPageScrollElement] = useState<
    HTMLElement | undefined
  >()

  // Every page shares this scrollport and the browser only resets scroll for
  // the document, not for a nested scroller that stays mounted, so a deeply
  // scrolled explore list would open the next page mid-content. Reset before
  // paint, and only on pathname changes so filters kept in search params don't
  // jump the page.
  useLayoutEffect(() => {
    const scrollable = scrollableRef.current
    if (scrollable) {
      scrollable.scrollTop = 0
    }
  }, [pathname])

  useLayoutEffect(() => {
    if (isExplorePage) {
      setPageScrollElement(scrollableRef.current ?? undefined)
      return
    }

    setPageScrollElement(undefined)
  }, [isExplorePage])

  useEffectOnce(() => {
    return scrollPageToTopEvent.onCall(() => {
      scrollableRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
    })
  })

  return (
    <Root>
      {withTitleBar ? <AppTitleBar /> : undefined}
      <ScrollableArea ref={scrollableRef}>
        <TopWrapper $expanded={locked === 'expanded'}>
          <Topbar />
        </TopWrapper>
        <MainWrapper $expanded={locked === 'expanded'}>
          <Sidebar />
          <OutletPanel>
            <OutletInner>
              <PageScrollContext.Provider value={pageScrollElement}>
                <Outlet />
              </PageScrollContext.Provider>
            </OutletInner>
          </OutletPanel>
          <StatusBar />
        </MainWrapper>
      </ScrollableArea>
      <TauriEnvironment>
        <FavoritesWrapper $expanded={expanded}>
          <FavoritesDrawer />
        </FavoritesWrapper>
      </TauriEnvironment>
      <TradeSettingsModalController />
      <CreateCurrencyModal />
      <OtpDialogController />
      <SearchModal />
      <PremiumBuyDialogs />
      <SplashPointsInfoDialog />
    </Root>
  )
}

const Root = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
  isolation: isolate;

  background: var(--appearance-1);
`

const ScrollableArea = styled.div.attrs(() => ({
  style: { containerType: 'size' },
}))`
  flex: 1;
  min-height: 0;
  min-width: 0;

  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: none;
  will-change: scroll-position;

  &::-webkit-scrollbar {
    display: none;
  }
`

const TopWrapper = styled.div<{ $expanded: boolean }>`
  position: sticky;
  top: 0;
  z-index: ${DESKTOP_LAYOUT_Z_INDEX};
  padding-left: ${({ $expanded }) =>
    $expanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH}px;
`

const MainWrapper = styled.div<{ $expanded: boolean }>`
  display: grid;
  grid-template-columns: ${({ $expanded }) =>
      $expanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH}px minmax(
      0,
      1fr
    );
  transition: grid-template-columns 0.3s ease;
  align-items: start;
  min-width: 0;
  /* Pull grid under topbar so the sticky sidebar column overlays it; see pointer-events */
  margin-top: -${TOPBAR_HEIGHT}px;
  pointer-events: none;
`

const OutletPanel = styled.div`
  grid-column: 2;
  min-width: 0;
  pointer-events: none;
`

const OutletInner = styled.div`
  margin-top: ${TOPBAR_HEIGHT}px;
  min-width: 0;
  pointer-events: auto;
`

const FavoritesWrapper = styled.div<{ $expanded: boolean }>`
  position: fixed;
  inset: 0;
  z-index: ${DESKTOP_LAYOUT_Z_INDEX};
  overflow: visible;
  pointer-events: none;
  margin-top: var(--title-bar-height);
  height: calc(var(--viewport-height) - 24px);
  margin-left: ${({ $expanded }) =>
    $expanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH}px;

  & > * {
    pointer-events: auto;
  }
`
